import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  usePutProfiles,
  getProfilesById,
  getGetProfilesByIdQueryKey,
} from '@/api/control/control-plane/control-plane';
import { CreateProfileRequestDeleteMode } from '@/api/control/missionImpactAPI.schemas';
import { WizardStepper } from '@/components/profiles/wizard/WizardStepper';
import { getWizardSteps, WIZARD_STEP_IDS } from '@/components/profiles/wizard/wizardSteps';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, Loader2, AlertCircle } from 'lucide-react';
import { LoadingButton } from '@/components/LoadingButton';
import { useError } from '@/contexts/ErrorContext';
import { logger } from '@/services/logger';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Step 6: Configure Delete Mode
 * Sixth step in the profile creation wizard
 */
export default function ConfigureDeleteModeStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleApiError } = useError();
  const { t } = useLanguage();
  const { orgId, profileId } = useParams<{ orgId: string; profileId: string }>();

  if (!orgId || !profileId) {
    logger.error(new Error('Missing orgId or profileId in route params'), {
      context: 'ConfigureDeleteModeStep.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  const [selectedMode, setSelectedMode] = useState<string>('Soft');

  // Fetch profile to verify it exists and get current delete mode
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
    queryFn: async () => {
      const response = await getProfilesById(orgId, profileId);
      return response;
    },
    retry: 1,
  });

  const profile = profileData?.data;
  const hasDeleteMode = !!profile?.deleteMode;

  // Get wizard steps with proper completion status
  const wizardSteps = getWizardSteps(WIZARD_STEP_IDS.DELETE_MODE, {
    [WIZARD_STEP_IDS.DELETE_MODE]: hasDeleteMode,
  });

  // Set selected mode from profile data
  useEffect(() => {
    if (profile?.deleteMode) {
      setSelectedMode(profile.deleteMode);
    }
  }, [profile]);

  // Redirect if profile doesn't exist or if setup is incomplete
  useEffect(() => {
    if (!isLoadingProfile && profileData?.data) {
      if (!profileData.data.source) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/source`);
      } else if (!profileData.data.target) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/target`);
      }
    } else if (!isLoadingProfile && !profileData?.data) {
      void navigate(`/dashboard/orgs/${orgId}/profiles/wizard/profile`);
    }
  }, [isLoadingProfile, profileData, navigate, orgId, profileId]);

  // Update profile mutation
  const { mutate: updateProfile, isPending: isUpdating } = usePutProfiles({
    mutation: {
      onSuccess: () => {
        toast.success(t.wizard.deleteMode.successMessage);
        void queryClient.invalidateQueries({ queryKey: getGetProfilesByIdQueryKey(orgId, profileId) });
        logger.trackEvent('DeleteMode.Configured', {
          orgId,
          profileId,
          deleteMode: selectedMode,
        });
        // Navigate to initial sync step
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/initial-sync`);
      },
      onError: (error) => {
        handleApiError(error, { context: 'ConfigureDeleteModeStep.updateProfile', profile: profileData?.data });
      },
    },
  });

  const handleSave = () => {
    if (!profile) return;

    updateProfile({
      orgId,
      profileId,
      data: {
        name: profile.name,
        regionId: profile.regionId,
        isEnabled: profile.isEnabled,
        deleteMode: selectedMode as CreateProfileRequestDeleteMode,
      },
    });
  };

  if (isLoadingProfile) {
    return (
      <div className="space-y-6">
        <WizardStepper steps={wizardSteps} />
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Wizard Stepper */}
      <WizardStepper steps={wizardSteps} />

      {/* Page Header */}
      <div className="mx-auto text-center">
        <h1 className="text-3xl font-bold">
          {t.wizard.deleteMode.title}
        </h1>
        <p className="text-muted-foreground">
          {t.wizard.deleteMode.description}
        </p>
      </div>

      {/* Form Card */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>{t.wizard.deleteMode.cardTitle}</CardTitle>
          <CardDescription>
            {t.wizard.deleteMode.cardDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Information Alert */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {t.wizard.deleteMode.infoMessage}
              </AlertDescription>
            </Alert>

            {/* Warning if already configured */}
            {profile?.hasTriggeredInitialSync && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{t.wizard.deleteMode.warningAfterInitialSync}</AlertDescription>
              </Alert>
            )}

            {/* Delete Mode Options */}
            <RadioGroup value={selectedMode} onValueChange={setSelectedMode} className="space-y-4">
              {/* Soft Delete */}
              <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent transition-colors">
                <RadioGroupItem value="Soft" id="soft" className="mt-1" />
                <div className="flex-1 space-y-1">
                  <Label htmlFor="soft" className="text-base font-semibold cursor-pointer">
                    {t.wizard.deleteMode.softDelete}
                    <span className="ml-2 text-xs font-normal text-muted-foreground px-2 py-0.5 background-success text-success rounded">
                      {t.wizard.deleteMode.recommended}
                    </span>
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t.wizard.deleteMode.softDeleteDescription}
                  </p>
                </div>
              </div>

              {/* Hard Delete */}
              <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent transition-colors">
                <RadioGroupItem value="HardTombstone" id="hard-delete" className="mt-1" />
                <div className="flex-1 space-y-1">
                  <Label htmlFor="hard-delete" className="text-base font-semibold cursor-pointer">
                    {t.wizard.deleteMode.hardDelete}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t.wizard.deleteMode.hardDeleteDescription}
                  </p>
                </div>
              </div>
            </RadioGroup>

            {/* Actions */}
            <div className="flex justify-end pt-4 border-t">
              <LoadingButton type="button" onClick={handleSave} isLoading={isUpdating}>
                {t.wizard.deleteMode.nextRunInitialSync}
              </LoadingButton>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
